import React, { useState } from "react";
import { Check, Star, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";

const Row = ({ l, active, busy, onToggle }) => (
  <button onClick={() => onToggle(l.code)} disabled={busy} data-testid={`lc-settings-lang-${l.code}`}
    className={`flex items-center gap-3 rounded-xl border p-3.5 text-left transition ${
      active ? "border-brand-amber/50 bg-brand-amber/5" : "border-line bg-bg-surface hover:border-ink-muted/40"}`}>
    <span className="font-display text-sm font-bold uppercase text-ink-primary w-10 shrink-0">{l.code}</span>
    <span className="flex-1">
      <span className="block text-sm font-semibold text-ink-primary">{l.name}</span>
      <span className="block text-xs text-ink-muted">{l.native}</span>
    </span>
    {busy ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> : active && <Check className="h-4 w-4 text-brand-amber" />}
  </button>
);

export const LangSettingsTab = ({ overview, stationId, refresh }) => {
  const [busy, setBusy] = useState("");
  if (!overview) return null;
  const { languages, active_languages: active, auto_translate } = overview;

  const patch = async (payload, busyKey) => {
    setBusy(busyKey);
    try {
      await api.patch("/language-center/settings", { station_id: stationId, ...payload });
      await refresh();
    } catch {
      toast.error("Could not save settings");
    } finally { setBusy(""); }
  };

  const toggleLang = (code) => {
    const next = active.includes(code) ? active.filter((c) => c !== code) : [...active, code];
    if (!next.length) return toast.error("Keep at least one language active");
    patch({ active_languages: next }, code);
  };

  return (
    <div data-testid="lc-settings-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">Language Settings</h2>
      <p className="text-sm text-ink-secondary mb-6">Configure your multilingual content preferences</p>

      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-8 flex items-center gap-4" data-testid="lc-autotranslate-row">
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink-primary">Auto-Translate Content</div>
          <div className="text-xs text-ink-muted mt-0.5">Automatically generate content in all active languages</div>
        </div>
        <Switch
          checked={auto_translate} data-testid="lc-autotranslate-switch"
          onCheckedChange={(v) => patch({ auto_translate: v }, "auto")}
        />
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6">
        <h3 className="type-section-heading text-ink-primary">Phase 1 — World Languages</h3>
        <p className="text-xs text-ink-muted mb-4">Core multilingual support for global radio stations</p>
        <div className="grid md:grid-cols-2 gap-3">
          {[...languages.launch, ...languages.phase1].map((l) => (
            <Row key={l.code} l={l} active={active.includes(l.code)} busy={busy === l.code} onToggle={toggleLang} />
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-5">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-emerald-400" />
          <h3 className="type-section-heading text-ink-primary">Phase 2 — Caribbean Languages</h3>
        </div>
        <p className="text-xs text-ink-muted mb-4">Unique support for Caribbean broadcasters — industry first</p>
        <div className="grid md:grid-cols-2 gap-3">
          {languages.caribbean.map((l) => (
            <Row key={l.code} l={l} active={active.includes(l.code)} busy={busy === l.code} onToggle={toggleLang} />
          ))}
        </div>
      </div>
    </div>
  );
};
